// import { setStripeCustomerId } from './queries'
import { getAxiosApiClientByUser } from './axiosInstance'
import { updateUserInfo } from './user-service'
import { handleApiError } from '@/utils/utils'

export interface ResponseGetBillingInfo {
  status: string
  data: ResponseGetBillingInfoData
}

export interface ResponseGetBillingInfoData {
  first_name: string
  last_name: string
  zip_code: string
  country: string
  state: string
  city: string
  street_address: string
  street_address_2: string
}

interface ResponseCreateStripeCustomer {
  customerId: string
}

export interface ResponseGetUserPlanData {
  plan: string
  price: number
  billing_frequency: string
  is_trial:boolean
  next_charge: string
  trial_end_date: string
  days_remaining:number
  payment_method: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
  status: string
  auto_renews: boolean
  cancel_at: string | number | null
  hasSubscription: boolean
}

interface ResponseGetUserPlan {
  status: string
  data:
    | ResponseGetUserPlanData
    | {
        has_subscription: boolean
      }
}

interface ResponseGetUserInovicesData {
  invoice_number: string
  date: string
  product_name: string
  amount: number
  pdf_url: string
}

interface ResponseGetUserInovicesPagination {
  current_page: number
  items_per_page: number
  total_pages: number
  has_more: boolean
}

interface ResponseGetUserInovices {
  status: string
  data: ResponseGetUserInovicesData[]
  pagination: ResponseGetUserInovicesPagination
}

interface StripeSubscriptionResponse {
  url: string
  error?: string
  clientSecret?: string
  requires_action?: boolean
  client_secret?: string
  subscription_id?: string
  success?: boolean
  message?: string
}

export interface CreatePaymentIntentRequest {
  planName: string
  userId: string
  agencyId: string
  isComingFromGrapes: boolean
  paymentMethodId?: string
}

export interface ResponseCancelSubscription {
  status: string
  message: string
  subscription_id: 'string'
}

interface ResponseGetPlanUpdate {
  status: boolean
  message: string
  data: {
    subscription_id: string
    plan_type: string
    period: string
    current_period_end: string
  }
}

export interface ResponseProvideSubscriptionCancellationReason {
  status: string
  message: string
}

export interface ResponseRenewSubscription {
  status: string
  message: string
  subscription_id: string
}

interface ProrationDetail {
  description: string
  amount: number
  period_start: string
  period_end: string
}

interface PlanFeatures {
  credits: number
  liveChat: boolean
  abTesting: boolean
  emailSupport: boolean
  landingPages: number
  'email-sending': number
  'image-editing': number
  'object-removal': number
  'image-generation': number
  'web-page-editing': number
  standardTemplates: boolean
  sender_email_limit: number
  'web-page-generation': number
  'web-page-generation-multi': number
  'web-page-generation-landing': number
}

export interface ResponsePlanProrationData {
  plan_name: string
  plan_features: PlanFeatures
  current_plan: string | null
  has_proration: boolean
  proration_details: ProrationDetail[]
  total_proration_amount: number
  proration_date: string
  next_billing_date: string
  invoice_total: number
  invoice_subtotal: number
  new_plan_price: number
  message: string
  is_trial:boolean
  sub_total?: number
}

interface ResponsePlanProration {
  status: string
  data: ResponsePlanProrationData
}

export const createFreeTrialSubscription = async ({
  paymentMethodId,
  userId,
  agencyId
}: {
  paymentMethodId: string
  userId: string
  agencyId: string
}): Promise<StripeSubscriptionResponse> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/stripe/create-free-trial', {
      payment_method_id: paymentMethodId,
      user_id: userId,
      agency_id: agencyId
    })

    return response.data
  } catch (error) {
    console.error('💳 Error creating free trial subscription:', error)

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      if (axiosError.response?.data?.detail) {
        throw new Error(axiosError.response.data.detail)
      }
    }

    throw error instanceof Error ? error : new Error('Unknown error occurred')
  }
}
export const createStripeCustomer = async (
  userId: string,
  userEmail: string
): Promise<ResponseCreateStripeCustomer | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<ResponseCreateStripeCustomer>(
      `/stripe/create-customer`,
      {
        user_id: userId,
        user_email: userEmail
      }
    )
    return response.data
  } catch (error) {
    // console.log('error creating stripe customer', error)
    throw new Error(`Error creating customer: ${JSON.stringify(error)}`)
  }
}

export const getBillingInfo = async (
  userId: string,
  email: string,
  stripeCustomerId: string | null
): Promise<ResponseGetBillingInfo | undefined | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    if (!stripeCustomerId) {
      const stripeCustomerResponse = await createStripeCustomer(userId, email)

      if (!stripeCustomerResponse) {
        // console.error("Couldn't create Stripe customer")
        return null
      }

      const { customerId } = stripeCustomerResponse

      const updateStripeCustomerIdResponse = await updateUserInfo({
        stripeCustomerId: customerId
        // email: email
      })

      if (updateStripeCustomerIdResponse.success) {
        const response = await apiClient.get<ResponseGetBillingInfo>(
          `/stripe/billing-info/${userId}`
        )
        return response.data
      } else {
        throw new Error(updateStripeCustomerIdResponse.error)
      }
    } else if (stripeCustomerId) {
      const response = await apiClient.get<ResponseGetBillingInfo>(
        `/stripe/billing-info/${userId}`
      )
      return response.data
    } else {
      throw new Error('Something went wrong')
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // console.error(error.message, 'error message')
    throw new Error('Getting billing info error:', error.message)
  }
}

export const getUserPlan = async (userId: string) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get<ResponseGetUserPlan>(
      `/stripe/plan/${userId}`
    )
    return response.data
  } catch (error) {
    throw new Error(`Getting user plan error: ${error}`)
  }
}

export const getUserInvoices = async (
  userId: string,
  page: number = 1,
  limit: number = 10
) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get<ResponseGetUserInovices>(
      `/stripe/invoices/${userId}`,
      {
        params: { page, limit }
      }
    )
    return response.data
  } catch (error) {
    throw new Error(`Getting user invoices error: ${error}`)
  }
}

export const subscribeToPlan = async (
  planName: string,
  userId: string,
  agencyId: string,
  isComingFromGrapes: boolean
): Promise<StripeSubscriptionResponse> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    if (!planName || !userId || !agencyId) {
      throw new Error(
        'Missing required parameters: planName, userId, or agencyId'
      )
    }

    const normalizedPlanName = planName.toLowerCase()

    const baseUrl = '/stripe'
    const queryParams = new URLSearchParams({
      user_id: userId,
      agency_id: agencyId,
      isComingFromGrapes: String(isComingFromGrapes)
    })

    const endpoint =
      normalizedPlanName === 'promo'
        ? `${baseUrl}/promo-subscribe?${queryParams}`
        : `${baseUrl}/subscribe?${queryParams}&plan=${normalizedPlanName}`

    const response = await apiClient.get(endpoint)
    return response.data
  } catch (error) {
    const errorResponse: StripeSubscriptionResponse = {
      url: '',
      error:
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'response' in error
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response as any)?.data?.error || 'Unknown API error'
            : 'Failed to subscribe to plan'
    }

    return errorResponse
  }
}

// Create Payment Intent for subscription
export const createPaymentIntent = async (
  request: CreatePaymentIntentRequest
): Promise<StripeSubscriptionResponse> => {
  try {
    // console.log('💳 Stripe Service: createPaymentIntent called with:', request)

    const apiClient = await getAxiosApiClientByUser()
    const { planName, userId, agencyId, isComingFromGrapes } = request

    // console.log('💳 Stripe Service: Destructured params:', {
    //   planName,
    //   userId,
    //   agencyId,
    //   isComingFromGrapes
    // })

    if (!planName || !userId || !agencyId) {
      // console.error('💳 Stripe Service: Missing required parameters:', {
      //   hasPlanName: !!planName,
      //   hasUserId: !!userId,
      //   hasAgencyId: !!agencyId
      // })
      throw new Error(
        'Missing required parameters: planName, userId, or agencyId'
      )
    }

    const normalizedPlanName = planName.toLowerCase()
    const baseUrl = '/stripe'

    const payload = {
      user_id: userId,
      agency_id: agencyId,
      plan: normalizedPlanName,
      isComingFromGrapes: String(isComingFromGrapes)
    }

    // console.log('💳 Stripe Service: Sending payload to backend:', payload)

    const endpoint =
      normalizedPlanName === 'promo'
        ? `${baseUrl}/setup-intent`
        : `${baseUrl}/setup-intent`

    // console.log('💳 Stripe Service: Calling endpoint:', endpoint)

    const response = await apiClient.post(endpoint, payload)
    // console.log('💳 Stripe Service: Response received:', response.data)
    return response.data
  } catch (error: unknown) {
    // console.error('Error creating payment intent:', error)
    return {
      url: '',
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create payment intent'
    }
  }
}

export const updateUserPlan = async (
  userId: string,
  planType: string,
  period: string
) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get<ResponseGetPlanUpdate>(
      `/stripe/update-plan/${userId}`,
      {
        params: {
          plan_type: planType,
          period
        }
      }
    )
    return response.data
  } catch (error) {
    throw new Error(`Update plan error: ${error}`)
  }
}

export const updatePaymentDetails = async (
  userId: string,
  paymentMethodId: string
): Promise<ResponseCreateStripeCustomer | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<ResponseCreateStripeCustomer>(
      `/stripe/update-payment-details/${userId}`,
      {
        payment_method_id: paymentMethodId
      }
    )
    return response.data
  } catch (error) {
    // console.error('Updating payment details error: ', error)
    throw new Error(`Updating payment details error: ${error}`)
  }
}

export const updateBillingInfo = async (
  userId: string,
  {
    first_name,
    last_name,
    zip_code,
    country,
    state,
    city,
    street_address,
    street_address_2
  }: ResponseGetBillingInfoData
): Promise<ResponseGetBillingInfo | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<ResponseGetBillingInfo>(
      `/stripe/update-billing-info/${userId}`,
      {
        first_name,
        last_name,
        zip_code,
        country,
        state,
        city,
        street_address,
        street_address_2
      }
    )
    return response.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new Error('Updating billing info error:', error)
  }
}

export const cancelSubscription = async (
  userId: string
): Promise<ResponseCancelSubscription | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<ResponseCancelSubscription>(
      `/stripe/cancel-subscription/${userId}`
    )
    return response.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new Error('Cancelling subscription error:', error)
  }
}

export const provideSubscriptionCancellationReason = async (
  subscriptionId: string,
  reason: string
): Promise<ResponseProvideSubscriptionCancellationReason | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.put<ResponseCancelSubscription>(
      `/stripe/update-cancellation-reason/${subscriptionId}`,
      { reason }
    )
    return response.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new Error('Updating cancellation reason error:', error)
  }
}

export const renewSubscription = async (
  userId: string
): Promise<ResponseRenewSubscription | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<ResponseRenewSubscription>(
      `/stripe/renew-subscription/${userId}`
    )
    return response.data
  } catch (error) {
    // console.error('Error renewing subscription:', error)
    throw error
  }
}

export const getStripeKeys = async () => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get('/stripe/get-keys')
    return response.data
  } catch (error) {
    handleApiError(error, 'Fetching Stripe Keys Failed')
  }
}

export const updateStripeKeys = async (payload: {
  test_publish_key?: string
  test_private_key?: string
  live_publish_key?: string
  live_private_key?: string
  stripe_mode?: string
}) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.put('/stripe/update-keys', payload)
    return response.data
  } catch (error) {
    handleApiError(error, 'Error updating stripe keys.')
  }
}

export const getSubscriptionPlans = async () => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get('/stripe/subscription-plans')
    return response.data.data
  } catch (error) {
    handleApiError(error, 'Failed to get subscription plan')
  }
}

export const fetchStripeProduct = async (stripeProductId: string) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get(
      `/stripe/fetch-stripe-product/${stripeProductId}`
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Error Fetching Stripe Product')
  }
}

export const createSubscription = async ({
  paymentMethodId,
  userId,
  agencyId,
  planName,
  isYearly
}: {
  paymentMethodId: string
  userId: string
  agencyId: string
  planName: string
  isYearly: boolean
}): Promise<StripeSubscriptionResponse> => {
  try {
    // console.log('💳 Creating subscription with payload:', {
    //   payment_method_id: paymentMethodId,
    //   user_id: userId,
    //   agency_id: agencyId,
    //   plan: planName,
    //   yearly: isYearly
    // })

    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/stripe/create-subscription', {
      payment_method_id: paymentMethodId,
      user_id: userId,
      agency_id: agencyId,
      plan: planName,
      yearly: isYearly
    })

    // console.log('💳 Subscription response:', response.data)
    return response.data
  } catch (error) {
    console.error('💳 Error creating subscription:', error)

    // Handle axios errors specifically
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      if (axiosError.response?.data?.detail) {
        throw new Error(axiosError.response.data.detail)
      }
    }

    throw error instanceof Error ? error : new Error('Unknown error occurred')
  }
}

export const getPlanProration = async (
  userId: string,
  planType: string,
  period: 'monthly' | 'yearly'
): Promise<ResponsePlanProration | null> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get(`/stripe/plan-proration/${userId}`, {
      params: {
        plan_type: planType,
        period
      }
    })
    return response.data
  } catch (error) {
    throw new Error(`Fetching plan proration error: ${error}`)
  }
}
